'use client';

import { useState, useEffect } from 'react';
import type { AuthModalMode } from '@/contexts/modal-context';
import { GoogleLoginButton } from '@/components/auth/GoogleLoginButton';
import { YandexLoginButton } from '@/components/auth/YandexLoginButton';
import { TelegramLoginButton } from '@/components/auth/TelegramLoginButton';
import { VkLoginButton } from '@/components/auth/VkLoginButton';
import { SmsLoginForm } from '@/components/auth/SmsLoginForm';
import { EmailOtpForm } from '@/components/auth/EmailOtpForm';

/**
 * Key used in localStorage to persist the pending guest message across OAuth
 * redirects. Shape: `{ text, chatId, model, timestamp }`. TTL is 10 minutes —
 * older entries are treated as expired and removed on read.
 *
 * Re-exported from `components/auth-modal.tsx` for public-API stability.
 */
export const GUEST_PENDING_MESSAGE_KEY = 'guest-pending-message';
export const GUEST_PENDING_TTL_MS = 10 * 60 * 1000;
interface GuestPendingMessage {
  text: string;
  chatId?: string;
  model?: string;
  timestamp: number;
}
function readGuestPendingMessage(): GuestPendingMessage | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(GUEST_PENDING_MESSAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GuestPendingMessage;
    if (!parsed || typeof parsed !== 'object' || typeof parsed.text !== 'string') {
      window.localStorage.removeItem(GUEST_PENDING_MESSAGE_KEY);
      return null;
    }
    const ts = typeof parsed.timestamp === 'number' ? parsed.timestamp : 0;
    if (Date.now() - ts >= GUEST_PENDING_TTL_MS) {
      window.localStorage.removeItem(GUEST_PENDING_MESSAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    try {
      window.localStorage.removeItem(GUEST_PENDING_MESSAGE_KEY);
    } catch {
      // storage unavailable — ignore
    }
    return null;
  }
}
export interface AuthModalContentProps {
  mode: AuthModalMode;
  callbackURL?: string;
  smsEnabled: boolean;
  /** Called on mount with the pending message text if localStorage has a fresh entry. */
  onPendingMessage?: (text: string) => void;
}

/**
 * Stateless unified auth layout shared by the popup `AuthModal` and the
 * `/sign-in` / `/sign-up` pages. Layout:
 *
 *   - Headline (copy depends on `mode`) + subtitle
 *   - Google large button (w-full)
 *   - Row of three round buttons: Yandex / Telegram / VK
 *   - [if `smsEnabled`] — «или» divider + SmsLoginForm
 *   - Legal footer (`/terms`, `/privacy-policy`)
 *
 * Responsive is pure CSS (`md:`). Do NOT use `useIsMobile()` here — Dialog
 * sizing stays the responsibility of the `AuthModal` wrapper.
 */
export function AuthModalContent({
  callbackURL,
  smsEnabled,
  onPendingMessage
}: AuthModalContentProps) {
  const [authError, setAuthError] = useState<string | null>(null);

  // Read pending guest message from localStorage on mount. Fires onPendingMessage
  // when the entry is fresh (< 10 min). Expired entries are silently cleared.
  useEffect(() => {
    const pending = readGuestPendingMessage();
    if (pending && onPendingMessage) {
      onPendingMessage(pending.text);
    }
    // Intentionally run once on mount — re-running would replay the same text.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const title = 'Войти или зарегистрироваться';
  return <div className="w-full max-w-[400px] mx-auto">
      {/* Header — крупный заголовок без подзаголовка
          (текст «используя социальную сеть» теперь в divider'е ниже) */}
      <div className="text-center mb-7">
        <h2 className="text-[22px] sm:text-[24px] font-bold tracking-[-0.012em] leading-[1.2] text-foreground">
          {title}
        </h2>
      </div>

      {/* Error */}
      {authError && <div role="alert" className="mb-4 p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/30 dark:text-red-400 rounded-xl text-center">
          {authError}
        </div>}

      {/* Primary providers — Google + Яндекс large stack */}
      <div className="flex flex-col gap-3">
        <GoogleLoginButton variant="large" callbackURL={callbackURL} onError={setAuthError} />
        <YandexLoginButton variant="large" callbackURL={callbackURL} onError={setAuthError} />
      </div>

      {/* Divider "используя социальную сеть" — две тонкие линии и текст по центру */}
      <div className="relative my-6" role="separator" aria-orientation="horizontal">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <span className="w-full border-t border-slate-200 dark:border-zinc-800" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-background px-3 text-[12px] uppercase tracking-[0.06em] text-muted-foreground/80">
            используя социальную сеть
          </span>
        </div>
      </div>

      {/* Secondary providers — Telegram + VK round circles */}
      <div className="flex items-center justify-center gap-4">
        <TelegramLoginButton variant="round" callbackURL={callbackURL} onError={setAuthError} />
        <VkLoginButton variant="round" callbackURL={callbackURL} onError={setAuthError} />
      </div>

      {/* SMS block — feature-flagged */}
      {smsEnabled && <>
          <div className="relative my-6" role="separator" aria-orientation="horizontal">
            <div className="absolute inset-0 flex items-center" aria-hidden="true">
              <span className="w-full border-t border-slate-200 dark:border-zinc-800" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-2 text-xs text-muted-foreground">
                или
              </span>
            </div>
          </div>
          <SmsLoginForm callbackURL={callbackURL} onError={setAuthError} />
        </>}

      {/* Email-OTP — всегда. Не за флагом: SMTP уже работает (retention),
          а транзакционные письма обходят warmup (skipWarmup в emailOTP plugin). */}
      <div className="relative my-6" role="separator" aria-orientation="horizontal">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <span className="w-full border-t border-slate-200 dark:border-zinc-800" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-background px-2 text-xs text-muted-foreground">
            или
          </span>
        </div>
      </div>
      <EmailOtpForm callbackURL={callbackURL} onError={setAuthError} />

      {/* Legal */}
      <p className="mt-7 text-[11px] text-center text-muted-foreground leading-relaxed">
        Продолжая, вы соглашаетесь с{' '}
        <a href="/terms" className="text-foreground/70 hover:text-foreground underline-offset-2 hover:underline transition-colors">
          Условиями использования
        </a>{' '}
        и{' '}
        <a href="/privacy-policy" className="text-foreground/70 hover:text-foreground underline-offset-2 hover:underline transition-colors">
          Политикой конфиденциальности
        </a>
      </p>
    </div>;
}